import React, { useEffect, useState, useCallback } from 'react';
import { getOptimizedImageUrl } from '../lib/imageOptimizer';

export default function LightboxModal({ images = [], initialIndex = 0, isOpen, onClose }) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [touchStartX, setTouchStartX] = useState(null);

  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex]);

  const handlePrev = useCallback(() => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  }, [images.length]);

  const handleNext = useCallback(() => {
    setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  }, [images.length]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'ArrowRight') handleNext();
    };

    window.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose, handlePrev, handleNext]);

  const handleTouchStart = (e) => {
    setTouchStartX(e.touches[0].clientX);
  };

  const handleTouchEnd = (e) => {
    if (touchStartX === null) return;
    const touchEndX = e.changedTouches[0].clientX;
    const diffX = touchStartX - touchEndX;

    if (diffX > 50) {
      handleNext();
    } else if (diffX < -50) {
      handlePrev();
    }
    setTouchStartX(null);
  };

  if (!isOpen || !images.length) return null;

  const currentImage = images[currentIndex];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Billedvisning"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-md animate-fadeIn select-none"
      onClick={onClose}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Close Button */}
      <button
        onClick={onClose}
        type="button"
        className="absolute top-4 right-4 z-50 flex items-center justify-center w-12 h-12 rounded-full bg-neutral-900/80 text-white hover:bg-neutral-800 transition-colors border border-neutral-700 focus:outline-none focus:ring-2 focus:ring-[#C5A059]"
        aria-label="Luk visning"
      >
        <i className="fa-solid fa-xmark text-lg" aria-hidden="true" />
      </button>

      {/* Prev Button */}
      {images.length > 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handlePrev();
          }}
          type="button"
          className="absolute left-4 top-1/2 -translate-y-1/2 z-50 flex items-center justify-center w-12 h-12 rounded-full bg-neutral-900/80 text-white hover:bg-[#C5A059] hover:text-neutral-950 transition-colors border border-neutral-700 focus:outline-none focus:ring-2 focus:ring-[#C5A059]"
          aria-label="Forrige billede"
        >
          <i className="fa-solid fa-chevron-left text-base" aria-hidden="true" />
        </button>
      )}

      {/* Next Button */}
      {images.length > 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleNext();
          }}
          type="button"
          className="absolute right-4 top-1/2 -translate-y-1/2 z-50 flex items-center justify-center w-12 h-12 rounded-full bg-neutral-900/80 text-white hover:bg-[#C5A059] hover:text-neutral-950 transition-colors border border-neutral-700 focus:outline-none focus:ring-2 focus:ring-[#C5A059]"
          aria-label="Næste billede"
        >
          <i className="fa-solid fa-chevron-right text-base" aria-hidden="true" />
        </button>
      )}

      {/* Image Display */}
      <div
        className="relative max-w-5xl max-h-[85vh] p-4 flex flex-col items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={getOptimizedImageUrl(currentImage, { width: 1600, quality: 90 })}
          alt={`Galleri billede ${currentIndex + 1} af ${images.length}`}
          className="max-h-[80vh] max-w-full object-contain rounded-lg shadow-2xl border border-neutral-800"
          loading="eager"
        />

        {/* Counter Badge */}
        {images.length > 1 && (
          <div className="mt-3 px-3.5 py-1 rounded-full bg-neutral-900/90 border border-neutral-800 text-xs font-medium text-[#DFBF77]">
            {currentIndex + 1} / {images.length}
          </div>
        )}
      </div>
    </div>
  );
}
